package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.owner.WorkplaceGroupDto
import com.evoapps.evoshift.data.mapper.EngagementMapper
import com.evoapps.evoshift.domain.model.GroupManager
import com.evoapps.evoshift.domain.model.GroupManagers
import com.evoapps.evoshift.domain.model.InvalidGroupManagersException
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.InvalidWorkplaceGroupsException
import com.evoapps.evoshift.domain.model.WorkplaceGroup
import com.evoapps.evoshift.domain.model.WorkplaceGroups
import com.evoapps.evoshift.domain.repository.WorkplaceGroupRepository
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlin.coroutines.cancellation.CancellationException

/**
 * SH-267: the owner reads workplace_groups by ownerId (firestore.rules let only them); saving is
 * the saveWorkplaceGroups callable (functions/workplaceGroups.js). A refusal whose
 * `details.reason` names a [WorkplaceGroups.Problem] comes back as [InvalidWorkplaceGroupsException].
 */
class WorkplaceGroupRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : WorkplaceGroupRepository {

    override suspend fun getGroups(ownerId: String): Result<List<WorkplaceGroup>> = guarded {
        firestore.collection("workplace_groups").where { "ownerId" equalTo ownerId }.get()
            .documents.map { toDomain(it.data<WorkplaceGroupDto>().copy(id = it.id)) }
    }

    override suspend fun saveGroups(groups: List<WorkplaceGroup>): Result<List<WorkplaceGroup>> = guarded {
        val result = functions.call("saveWorkplaceGroups", buildJsonObject {
            putJsonArray("groups") {
                groups.forEach { group ->
                    add(buildJsonObject {
                        if (group.id.isNotBlank()) put("id", group.id)
                        put("name", group.name)
                        putJsonArray("workplaceIds") { group.workplaceIds.forEach { add(JsonPrimitive(it)) } }
                    })
                }
            }
        })
        val ownerId = groups.firstOrNull()?.ownerId.orEmpty()
        ((result as? JsonObject)?.get("groups") as? JsonArray)?.map { element ->
            val g = element.jsonObject
            WorkplaceGroup(
                id = g["id"]?.jsonPrimitive?.contentOrNull.orEmpty(),
                ownerId = ownerId,
                name = g["name"]?.jsonPrimitive?.contentOrNull.orEmpty(),
                workplaceIds = g["workplaceIds"]?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull }.orEmpty(),
                isDefault = g["isDefault"]?.jsonPrimitive?.booleanOrNull ?: false
            )
        }?.map { saved ->
            // The layout's callable does not return the managers (it keeps them): ours are current.
            saved.copy(managers = groups.firstOrNull { it.id == saved.id }?.managers.orEmpty())
        } ?: groups
    }

    override suspend fun saveManagers(groupId: String, managers: List<GroupManager>): Result<List<GroupManager>> = try {
        val result = functions.call("saveGroupManagers", buildJsonObject {
            put("groupId", groupId)
            putJsonArray("managers") {
                managers.forEach { manager ->
                    add(buildJsonObject {
                        put("userId", manager.userId)
                        put("permission", manager.permission.name)
                    })
                }
            }
        })
        val saved = ((result as? JsonObject)?.get("managers") as? JsonArray)?.mapNotNull { element ->
            val m = element as? JsonObject ?: return@mapNotNull null
            val userId = m["userId"]?.jsonPrimitive?.contentOrNull ?: return@mapNotNull null
            GroupManager(userId, EngagementMapper.permissionOf(m["permission"]?.jsonPrimitive?.contentOrNull.orEmpty()))
        }
        Result.success(saved ?: managers)
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        val reason = ((e.details as? JsonObject)?.get("reason"))?.jsonPrimitive?.contentOrNull
        val problem = GroupManagers.Problem.entries.firstOrNull { it.name == reason }
        Result.failure(if (problem != null) InvalidGroupManagersException(problem) else e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    private inline fun <T> guarded(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        val reason = ((e.details as? JsonObject)?.get("reason"))?.jsonPrimitive?.contentOrNull
        val problem = when (reason) {
            "MALFORMED" -> null
            else -> WorkplaceGroups.Problem.entries.firstOrNull { it.name == reason }
        }
        Result.failure(if (problem != null) InvalidWorkplaceGroupsException(setOf(problem)) else e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        fun toDomain(dto: WorkplaceGroupDto): WorkplaceGroup = WorkplaceGroup(
            id = dto.id,
            ownerId = dto.ownerId,
            name = dto.name,
            workplaceIds = dto.workplaceIds,
            isDefault = dto.isDefault,
            // By when they were added (then by id), so the list keeps its order between reads.
            managers = dto.managers.entries
                .sortedWith(compareBy({ it.value.addedAt.orEmpty() }, { it.key }))
                .map { (uid, manager) -> GroupManager(uid, EngagementMapper.permissionOf(manager.permission)) }
                .filter { it.permission in GroupManagers.LEVELS }
        )
    }
}
